import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { ArrowRight, Check, ExternalLink, AlertCircle, CreditCard } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Services() {
  const [services, setServices] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location.hash, services]);

  const serviceFeatures = {
    "tax-services": [
      "Federal & State tax preparation",
      "IRS e-file submission",
      "Maximum deduction identification",
      "Year-round tax planning",
    ],
    "immigration-notary": [
      "Immigration form assistance",
      "Document preparation",
      "Professional notarization",
      "Apostille services",
    ],
    "recruitment-staffing": [
      "Talent sourcing",
      "Candidate screening",
      "Interview coordination",
      "Placement services",
    ],
    "credit-repair": [
      "Credit report analysis",
      "Error identification",
      "Dispute filing",
      "Score improvement guidance",
    ],
    "business-development": [
      "Business formation",
      "Compliance assistance",
      "Document preparation",
      "Strategic planning",
    ],
    "website-design": [
      "Custom website design",
      "Mobile responsive layouts",
      "E-commerce solutions",
      "Ongoing maintenance",
    ],
    "bookkeeping": [
      "Financial record keeping",
      "Expense tracking",
      "Invoice management",
      "Monthly reports",
    ],
    "payroll": [
      "Payroll processing",
      "Tax withholding calculations",
      "Direct deposit setup",
      "Compliance management",
    ],
    "website-ai": [
      "Custom website design",
      "Mobile responsive",
      "AI automation solutions",
      "Ongoing support",
    ],
    "travel-booking": [
      "Flight reservations",
      "Hotel bookings",
      "Travel packages",
      "Trip planning",
    ],
    "admin-services": [
      "Fax services",
      "Printing & scanning",
      "Money transfers",
      "Document handling",
    ],
  };

  // Credit Report Section Component
  const CreditReportSection = () => (
    <div className="bg-gradient-to-br from-[#0F392B] to-[#1a5a45] rounded-2xl p-8 mt-8 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl" />
      
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center">
            <CreditCard className="w-6 h-6 text-[#0F392B]" />
          </div>
          <h3 className="font-heading text-2xl font-bold text-white">
            Pull Your Credit Report
          </h3>
        </div>
        
        <p className="font-body text-white/90 mb-4 max-w-2xl">
          Before your credit repair consultation, you'll need to obtain a copy of your credit report. 
          This allows us to review your credit history and identify areas for improvement.
        </p>
        
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <p className="font-body text-sm text-white/90">
            <strong>Important:</strong> Please complete your credit report before scheduling your consultation. 
            Having your report ready will help us provide you with the most accurate assessment and recommendations.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <a
            href="https://myfreescorenow.com/enroll/?AID=EspadyMultiServiceCo&PID=43212"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="credit-report-btn"
          >
            <Button className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-lg">
              Get Your Free Credit Report
              <ExternalLink className="w-5 h-5 ml-2" />
            </Button>
          </a>
          <Link to="/booking">
            <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0F392B] rounded-full px-6 py-3">
              Schedule Consultation
            </Button>
          </Link>
        </div>
        
        <p className="font-body text-xs text-white/60 mt-6">
          <span className="font-semibold">Disclaimer:</span> The credit report is processed through MyFreeScoreNow, 
          a third-party website. Espady Multi-Service Co. is not responsible for the content, privacy practices, 
          or services provided by this external site. Please review their terms before proceeding.
        </p>
      </div>
    </div>
  );

  return (
    <div data-testid="services-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-[#0F392B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D4AF37] blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="font-accent text-sm text-[#D4AF37] tracking-widest mb-4 block">
              OUR SERVICES
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Professional Services
              <br />
              <span className="text-[#D4AF37]">For Every Need</span>
            </h1>
            <p className="font-body text-white/80 text-lg max-w-2xl mx-auto">
              Comprehensive solutions designed to simplify your personal,
              financial, and business matters. All under one roof.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div key={service.id}>
                <div
                  id={service.id}
                  data-testid={`service-detail-${service.id}`}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? "lg:flex-row-reverse" : ""
                  }`}
                >
                  {/* Image */}
                  <div className={`relative ${index % 2 === 1 ? "lg:order-2" : ""}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-[350px] object-cover transform group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0F392B]/40 to-transparent" />
                    </div>
                    <div
                      className={`absolute -bottom-4 ${
                        index % 2 === 1 ? "-left-4" : "-right-4"
                      } w-32 h-32 bg-[#D4AF37]/20 rounded-2xl -z-10`}
                    />
                  </div>

                  {/* Content */}
                  <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                    <span className="font-accent text-xs text-[#D4AF37] tracking-widest mb-4 block">
                      SERVICE {String(index + 1).padStart(2, "0")}
                    </span>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F392B] mb-4">
                      {service.title}
                    </h2>
                    <div className="gold-line mb-6" />
                    <p className="font-body text-[#4A5568] leading-relaxed mb-6">
                      {service.fullDesc}
                    </p>

                    {/* Features */}
                    <ul className="space-y-3 mb-8">
                      {(serviceFeatures[service.id] || []).map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center gap-3">
                          <div className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-[#0F392B]" />
                          </div>
                          <span className="font-body text-sm text-[#4A5568]">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <Link to="/booking">
                      <Button
                        data-testid={`book-${service.id}-btn`}
                        className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-6 py-3 font-medium transition-all duration-300 hover:-translate-y-0.5"
                      >
                        Get Started
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Credit Report Section - Only show for Credit Repair service */}
                {service.id === "credit-repair" && <CreditReportSection />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services Highlight */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-accent text-sm text-[#D4AF37] tracking-widest mb-4 block">
              ALSO AVAILABLE
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F392B] mb-4">
              Additional Services
            </h2>
            <div className="gold-line mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Website Design */}
            <div className="bg-[#F9F9F7] rounded-2xl p-8 border border-transparent hover:border-[#D4AF37]/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#0F392B]/10 flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-[#0F392B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#0F392B] mb-3">
                Website Design
              </h3>
              <p className="font-body text-[#4A5568] text-sm mb-4">
                Professional website creation for your business. Modern, responsive designs that convert visitors into customers.
              </p>
              <Link to="/booking">
                <Button variant="outline" className="border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white rounded-full px-4 py-2 text-sm">
                  Book Consultation
                </Button>
              </Link>
            </div>

            {/* Bookkeeping */}
            <div className="bg-[#F9F9F7] rounded-2xl p-8 border border-transparent hover:border-[#D4AF37]/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#0F392B]/10 flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-[#0F392B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#0F392B] mb-3">
                Bookkeeping
              </h3>
              <p className="font-body text-[#4A5568] text-sm mb-4">
                Keep your finances organized with professional bookkeeping services. Track expenses, manage invoices, and stay compliant.
              </p>
              <Link to="/booking">
                <Button variant="outline" className="border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white rounded-full px-4 py-2 text-sm">
                  Book Consultation
                </Button>
              </Link>
            </div>

            {/* Payroll */}
            <div className="bg-[#F9F9F7] rounded-2xl p-8 border border-transparent hover:border-[#D4AF37]/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#0F392B]/10 flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-[#0F392B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#0F392B] mb-3">
                Payroll Services
              </h3>
              <p className="font-body text-[#4A5568] text-sm mb-4">
                Streamline your payroll process. We handle calculations, tax withholdings, and ensure timely payments for your team.
              </p>
              <Link to="/booking">
                <Button variant="outline" className="border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white rounded-full px-4 py-2 text-sm">
                  Book Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#F9F9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F392B] mb-4">
            Not Sure Which Service You Need?
          </h2>
          <p className="font-body text-[#4A5568] mb-8 max-w-2xl mx-auto">
            Contact us for a free consultation. Our team will help you identify
            the best solutions for your specific situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                data-testid="contact-us-btn"
                className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-8 py-6 text-lg font-medium"
              >
                Contact Us
              </Button>
            </Link>
            <Link to="/booking">
              <Button
                data-testid="book-consultation-btn"
                variant="outline"
                className="border-2 border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white rounded-full px-8 py-6 text-lg"
              >
                Book a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
